import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CoverageWaitlistForm from "@/components/CoverageWaitlistForm";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { LANDING } from "@/constants/testIds";
import {
  HeartHandshake, ShieldCheck, Sparkles, Star, MapPin,
  BadgeCheck, MessageCircle, CalendarClock, Filter, ArrowRight, GraduationCap,
  Moon, Plane, PartyPopper, Users, HomeIcon
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/4934161/pexels-photo-4934161.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CAREGIVER_IMG = "https://images.pexels.com/photos/8612920/pexels-photo-8612920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AVATAR_IMG = "https://images.unsplash.com/photo-1560707854-fb9a10eeaace?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBmcmllbmRseSUyMGJhYnlzaXR0ZXJ8ZW58MHx8fHwxNzg0ODE1OTEwfDA&ixlib=rb-4.1.0&q=85";

const CERTS = ["RBT", "BCBA", "BCaBA", "CPR", "First Aid", "CPI", "PCM", "Safety Care", "OT Assistant", "Special Ed"];

const RESPITE_CARDS = [
  {
    key: "traditional_respite",
    title: "Traditional respite",
    body: "Short-term in-home care that gives caregivers a break — routines, sensory tools, and calming strategies honored from minute one.",
    icon: <HomeIcon className="w-5 h-5" />,
    accent: "from-sage-100 to-sage-50",
    ring: "ring-sage-200",
  },
  {
    key: "overnight_support",
    title: "Overnight support",
    body: "Sitters trained for bedtime routines, night-waking, sleep disruptions, and safe supervision through the night.",
    icon: <Moon className="w-5 h-5" />,
    accent: "from-indigo-100 to-sage-50",
    ring: "ring-indigo-200",
  },
  {
    key: "travel_assistance",
    title: "Travel assistance",
    body: "Support at airports, on road trips, or at appointments — help with transitions, wait times, and unfamiliar environments.",
    icon: <Plane className="w-5 h-5" />,
    accent: "from-ochre-100 to-sage-50",
    ring: "ring-ochre-200",
  },
  {
    key: "party_buffers",
    title: "Party buffers",
    body: "A calm, familiar 1:1 companion for birthdays, weddings, and events — keeping your child regulated in high-stimulation settings.",
    icon: <PartyPopper className="w-5 h-5" />,
    accent: "from-rose-100 to-sage-50",
    ring: "ring-rose-200",
  },
  {
    key: "community_support",
    title: "Community support",
    body: "Grocery runs, library visits, playgrounds, and community outings — with a sitter who knows your child's triggers and cues.",
    icon: <Users className="w-5 h-5" />,
    accent: "from-emerald-100 to-sage-50",
    ring: "ring-emerald-200",
  },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/sitters-featured").then(({ data }) => setFeatured(data.results || [])).catch(() => {});
  }, []);
  const landingJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "AutismBabysitters",
    "description": "Marketplace of specialized babysitters trained in ABA, sensory support, and behavioral care for children on the autism spectrum.",
    "url": "https://autismbabysitters.com/",
    "priceRange": "$$",
    "areaServed": "United States",
    "makesOffer": RESPITE_CARDS.map((c) => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": c.title,
        "description": c.body,
        "url": `https://autismbabysitters.com/search?respite_services=${c.key}`,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-stone2-50">
      <SEO
        title="AutismBabysitters — Specialized sitters who truly understand your child"
        description="A calmer way to find care. Vetted RBTs, BCBAs, and CPR-certified specialists trained in sensory support, calming strategies, and behavioral care — for traditional respite, overnight, travel, party, and community support."
        canonical="https://autismbabysitters.com/"
        jsonLd={landingJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="grain-bg relative overflow-hidden" data-testid={LANDING.hero}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 animate-fade-in-up">
            <span className="section-eyebrow">A calmer way to find care</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-sage-900">
              Sitters who <span className="text-ochre-600">truly</span> understand your child.
            </h1>
            <p className="mt-6 text-lg text-stone2-600 max-w-xl leading-relaxed">
              A gentle, professional marketplace connecting families on the spectrum with vetted RBTs, BCBAs,
              special-education teachers, and specialized caregivers — matched by comfort, certification, and care.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register?role=family" data-testid={LANDING.ctaFamily} className="btn-primary">
                <HeartHandshake className="w-4 h-4" /> I'm a family <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/register?role=sitter" data-testid={LANDING.ctaSitter} className="btn-outline-sage">
                <GraduationCap className="w-4 h-4" /> I'm a sitter
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-5 text-sm text-stone2-600">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-sage-600" /> Background checked
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-sage-600" /> CPR certified
              </div>
              <div className="hidden md:flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-ochre-500" /> ABA trained
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden border border-stone2-200 shadow-sm">
              <img src={HERO_IMG} alt="Parent playing with child using colorful blocks" className="w-full h-[420px] object-cover" />
            </div>
            <div className="hidden sm:flex absolute -bottom-6 -left-6 paper-card px-4 py-3 gap-3 items-center shadow-md">
              <img src={AVATAR_IMG} alt="" className="w-11 h-11 rounded-full object-cover" />
              <div>
                <div className="flex items-center gap-1 text-sage-900 font-semibold text-sm">
                  Maya, RBT <BadgeCheck className="w-4 h-4 text-sage-600" />
                </div>
                <div className="flex items-center gap-1 text-xs text-stone2-600">
                  <Star className="w-3.5 h-3.5 fill-ochre-500 text-ochre-500" /> 4.9 · 2 miles away
                </div>
              </div>
            </div>
            <div className="hidden sm:flex absolute -top-4 -right-4 chip-ochre shadow-sm">Verified in 48 hours</div>
          </div>
        </div>
      </section>

      {/* Certifications marquee */}
      <section className="border-y border-stone2-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-[0.22em] text-stone2-400 font-semibold mr-2">Trusted credentials</span>
          {CERTS.map((c) => (
            <span key={c} className="chip" data-testid={`cert-chip-${c}`}>{c}</span>
          ))}
        </div>
      </section>

      {/* Respite service categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24" data-testid="landing-respite-categories">
        <div className="max-w-3xl">
          <span className="section-eyebrow">Care that fits your life</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-bold text-sage-900">
            Five kinds of respite. <span className="text-ochre-600">One trusted network.</span>
          </h2>
          <p className="mt-4 text-stone2-600 max-w-2xl">
            Not every moment needs the same kind of support. Choose the category that fits — we'll surface sitters trained and ready for exactly that.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESPITE_CARDS.map((card, i) => (
            <Link
              key={card.key}
              to={`/search?respite_services=${card.key}`}
              data-testid={`respite-card-${card.key}`}
              className={`group relative overflow-hidden rounded-3xl border border-stone2-200 bg-gradient-to-br ${card.accent} p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start justify-between">
                <span className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-sage-700 ring-1 ${card.ring} shadow-sm`}>
                  {card.icon}
                </span>
                <ArrowRight className="w-5 h-5 text-sage-700 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300" />
              </div>
              <h3 className="mt-6 font-heading font-semibold text-sage-900 text-xl leading-snug">
                {card.title}
              </h3>
              <p className="mt-3 text-sm text-stone2-700 leading-relaxed">
                {card.body}
              </p>
              <div className="mt-6 text-xs font-bold uppercase tracking-[0.22em] text-sage-700 flex items-center gap-1.5">
                Browse sitters
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}

          {/* Combined CTA card */}
          <Link
            to="/search"
            data-testid="respite-card-all"
            className="group relative overflow-hidden rounded-3xl border-2 border-dashed border-sage-300 bg-white p-7 hover:border-sage-500 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-center"
          >
            <div className="flex items-center gap-2 text-sage-700">
              <Filter className="w-5 h-5" />
              <span className="section-eyebrow !mt-0">All filters</span>
            </div>
            <h3 className="mt-4 font-heading font-semibold text-sage-900 text-xl leading-snug">
              Not sure which one?
            </h3>
            <p className="mt-3 text-sm text-stone2-600 leading-relaxed">
              Explore every sitter and mix filters — certifications, availability, ABA training, transport, allergies, and more.
            </p>
            <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ochre-600 group-hover:gap-2 transition-all">
              Open full search <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      </section>

      {/* How it works */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="landing-featured-sitters">
          <div className="flex items-end justify-between mb-8 gap-3 flex-wrap">
            <div>
              <span className="section-eyebrow">Recently active</span>
              <h2 className="mt-2 text-3xl font-heading font-bold text-sage-900">Meet a few of your specialized sitters</h2>
              <p className="text-stone2-600 text-sm mt-1 max-w-lg">
                Real, vetted caregivers accepting families right now.
              </p>
            </div>
            <Link to="/search" className="text-sm font-semibold text-sage-700 hover:text-sage-900">
              See all sitters →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((s) => (
              <Link key={s.id || s.user_id} to={`/sitters/${s.id || s.user_id}`}
                data-testid={`featured-sitter-${s.id || s.user_id}`}
                className="paper-card p-5 hover:-translate-y-1 transition-all duration-200 flex flex-col">
                <div className="flex items-center gap-3">
                  {s.photo_url ? (
                    <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-sage-100 shrink-0">
                      <img
                        src={s.photo_url}
                        alt={s.name}
                        className={`w-full h-full object-cover ${s.photo_blurred ? "blur-md scale-110" : ""}`}
                        style={s.photo_blurred ? { filter: "blur(10px)" } : undefined}
                      />
                      {s.photo_blurred && (
                        <div className="absolute inset-0 flex items-center justify-center bg-sage-900/10 text-[10px] font-semibold text-white/95 tracking-wide">
                          <span className="bg-sage-900/60 px-1.5 py-0.5 rounded-full backdrop-blur-sm">privacy</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center font-heading font-bold text-lg shrink-0">
                      {(s.name || "?").slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sage-900 truncate">{s.name}</div>
                    <div className="text-xs text-stone2-500 truncate">
                      {[s.city, s.state].filter(Boolean).join(", ") || "Nearby"}
                      {s.experience_years ? ` · ${s.experience_years} yr exp` : ""}
                    </div>
                  </div>
                </div>
                {s.elevator_pitch && (
                  <blockquote className="mt-3 border-l-2 border-ochre-500 pl-3 text-sm text-sage-900 italic leading-snug line-clamp-3">
                    "{s.elevator_pitch}"
                  </blockquote>
                )}
                <div className="mt-3 flex flex-wrap gap-1">
                  {(s.certifications || []).slice(0, 3).map((c) => <span key={c} className="chip">{c}</span>)}
                  {s.aba_trained && <span className="chip-ochre">ABA</span>}
                </div>
                <div className="mt-auto pt-3 flex items-center justify-between text-xs text-stone2-500">
                  <span>{s.hourly_rate ? `From $${s.hourly_rate}/hr` : ""}</span>
                  {s.verified && <span className="inline-flex items-center gap-1 text-sage-700 font-semibold">✓ Verified</span>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <span className="section-eyebrow">How it works</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-bold text-sage-900">
            Three quiet steps to a great fit.
          </h2>
          <p className="mt-4 text-stone2-600">
            Deep profiles, precise filters, and a shared sensory language — so every sitter arrives already knowing
            what soothes your child.
          </p>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
          {[
            { icon: <Filter className="w-5 h-5" />, title: "Filter by care needs", body: "18+ filters: RBT, ABA, gender, overnight, comfortable with aggressive behaviors, allergies, transport…" },
            { icon: <MapPin className="w-5 h-5" />, title: "Match nearby sitters", body: "Distance-aware search prioritizes verified sitters within your service radius." },
            { icon: <CalendarClock className="w-5 h-5" />, title: "Book & pay in-app", body: "Request time, chat in real-time, and pay securely with Stripe when accepted." },
          ].map((step, i) => (
            <div key={step.title} className="paper-card p-6 hover:-translate-y-1 transition-transform duration-200">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sage-50 text-sage-700 mb-4">
                {step.icon}
              </span>
              <div className="font-heading font-semibold text-sage-900 text-lg">{step.title}</div>
              <p className="mt-2 text-sm text-stone2-600 leading-relaxed">{step.body}</p>
              <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-ochre-600">Step {i + 1}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Split feature */}
      <section className="bg-sage-50/50 border-y border-stone2-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <img src={CAREGIVER_IMG} alt="Caregiver playing with child in a calm playroom" className="w-full h-[420px] object-cover rounded-3xl border border-stone2-200" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <span className="section-eyebrow">Child profile that travels with you</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-bold text-sage-900">
              Say it once. Everyone who cares will know.
            </h2>
            <p className="mt-4 text-stone2-600 max-w-lg">
              Capture triggers, calming strategies, sensory preferences, medications, elopement risk, and behavioral
              support plans in one calm profile. Share safely with any sitter you book.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                "Sensory & communication style", "Triggers & calming strategies",
                "Allergies & medications", "Toileting & sleep routines",
                "Elopement risk level", "Behavioral support plan",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-sage-900">
                  <MessageCircle className="w-4 h-4 text-sage-600 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/register?role=family" data-testid={LANDING.ctaFindSitter} className="mt-8 inline-flex btn-accent">
              Create your family profile <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage waitlist */}
      <section className="bg-ochre-50/40 border-b border-stone2-200" data-testid="landing-waitlist-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="section-eyebrow">Not in your area yet?</span>
              <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-bold text-sage-900">
                Don't miss it when coverage arrives.
              </h2>
              <p className="mt-4 text-stone2-600 leading-relaxed">
                We're actively vetting specialized sitters in every ZIP families ask for.
                Drop your info below and you'll be the first to know when a verified sitter joins your area — no spam, no fluff.
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm text-stone2-500">
                <div className="w-2 h-2 rounded-full bg-sage-500 animate-pulse" />
                <span>Currently onboarding in Austin, Atlanta, and expanding.</span>
              </div>
            </div>
            <CoverageWaitlistForm source="landing" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
